
import React, { useEffect, useRef, useState } from 'react';

type MotionProps = {
  defaultStyle: Record<string, number>;
  style: Record<string, number | { val: number; stiffness: number; damping: number }>;
  children: (interpolated: Record<string, number>) => React.ReactNode;
};

// Оптимизированная реализация анимации для совместимости с react-motion API
export const Motion: React.FC<MotionProps> = ({ defaultStyle, style, children }) => {
  const [interpolated, setInterpolated] = useState(defaultStyle);
  const frameRef = useRef<number>();
  const prevTimeRef = useRef<number>();
  const targetRef = useRef(style);
  const isRunningRef = useRef(false);

  useEffect(() => {
    // Обновляем целевое состояние
    targetRef.current = style;
    let needsAnimation = false;

    // Проверяем нужна ли анимация
    Object.keys(style).forEach(key => {
      const target = style[key];
      const current = interpolated[key];
      if (typeof target !== 'number') {
        needsAnimation = true;
      } else if (Math.abs(current - target) > 0.1) {
        needsAnimation = true;
      }
    });

    // Если анимация не нужна или уже запущена, выходим
    if (!needsAnimation && !isRunningRef.current) {
      return;
    }

    isRunningRef.current = true;

    const animate = (time: number) => {
      if (!prevTimeRef.current) {
        prevTimeRef.current = time;
        frameRef.current = requestAnimationFrame(animate);
        return;
      }

      const deltaTime = Math.min(time - prevTimeRef.current, 64) / 1000; // максимум 64ms (≈15fps)
      prevTimeRef.current = time;
      let shouldContinue = false;

      const newInterpolated = { ...interpolated };
      Object.keys(targetRef.current).forEach(key => {
        const target = targetRef.current[key];
        const current = newInterpolated[key];
        
        if (typeof target === 'number') {
          // Простая линейная анимация для числовых значений
          const diff = target - current;
          if (Math.abs(diff) < 0.1) {
            newInterpolated[key] = target;
          } else {
            newInterpolated[key] = current + diff * Math.min(deltaTime * 7, 0.9); // Ограничиваем скорость
            shouldContinue = true;
          }
        } else if (target && typeof target === 'object' && 'val' in target) {
          // Spring анимация
          const { val, stiffness = 170, damping = 26 } = target;
          const diff = val - current;
          
          if (Math.abs(diff) < 0.1) {
            newInterpolated[key] = val;
          } else {
            // Упрощенная версия spring анимации
            const speed = Math.min(deltaTime * stiffness / damping, 0.9); // ограничиваем шаг анимации
            newInterpolated[key] = current + diff * speed;
            shouldContinue = true;
          }
        }
      });

      setInterpolated(newInterpolated);

      if (shouldContinue) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        isRunningRef.current = false;
      }
    };

    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = undefined;
      }
    };
  }, [style, interpolated]);

  return <>{children(interpolated)}</>;
};
